import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Skeleton,
  Tabs,
  Tab,
  Badge,
  Paper,
  InputAdornment,
  Collapse,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Search,
  FilterList,
  TrendingUp,
  LocationOn,
  Schedule,
  AttachMoney,
  Star,
  Send,
  Visibility,
  ExpandMore,
  ExpandLess,
  Clear,
  CheckCircle,
  Cancel,
  Message
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import verifierService from '../services/verifierService';
import messagingService from '../services/messagingService';
import AIQuoteDialog from '../components/verifier/AIProposalDialog';
import useProfileGate from '../hooks/useProfileGate';
import ProfileGateDialog from '../components/ProfileGateDialog';
import DialogCloseButton from '../components/common/DialogCloseButton';

// Zimbabwe cities for location filter
const ZIMBABWE_CITIES = [
  'All Locations',
  'Harare',
  'Bulawayo',
  'Chitungwiza',
  'Mutare',
  'Gweru',
  'Kwekwe',
  'Kadoma',
  'Masvingo',
  'Chinhoyi',
  'Marondera',
  'Victoria Falls',
  'Hwange',
  'Bindura',
  'Zvishavane'
];

// Verification types
const VERIFICATION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'structural', label: 'Structural' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'finishing', label: 'Finishing' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'safety', label: 'Safety Inspection' },
  { value: 'progress', label: 'Progress Check' },
  { value: 'final', label: 'Final Inspection' }
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' }
];

// Date posted options
const DATE_POSTED_OPTIONS = [
  { value: '', label: 'Any Time' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' }
];

const VerifierMarketplacePage = ({ embedded = false, fixedTab = null }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { checkAction, showGateDialog, gateAction, closeGateDialog, profileComplete } = useProfileGate();
  const [activeTab, setActiveTab] = useState(fixedTab ?? 0);
  const [requests, setRequests] = useState([]);
  const [recommendedRequests, setRecommendedRequests] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    minFee: '',
    maxFee: '',
    city: '',
    verificationType: '',
    priority: '',
    datePosted: '',
    searchText: '',
    sortBy: 'created_at'
  });
  const [showFilters, setShowFilters] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Filters for My Responses tab
  const [quoteFilters, setQuoteFilters] = useState({
    status: '',
    searchText: '',
    minQuote: '',
    maxQuote: '',
    datePosted: '',
    sortBy: 'created_at'
  });
  const [showQuoteFilters, setShowQuoteFilters] = useState(true);
  const [quoteSearchInput, setQuoteSearchInput] = useState('');

  // Quote dialog
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Details dialog
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [detailsRequest, setDetailsRequest] = useState(null);

  // Quote details dialog
  const [quoteDetailsDialog, setQuoteDetailsDialog] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteVerificationRequest, setQuoteVerificationRequest] = useState(null);
  const [loadingQuoteDetails, setLoadingQuoteDetails] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    proposedFee: '',
    availableDate: '',
    message: ''
  });

  // Get verifier profile to determine tier
  const [verifierProfile, setVerifierProfile] = useState(null);
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const tabHelpText = [
    'Review client-posted verification work and send your price, availability, and message.',
    'See client requests that best match your profile and location.',
    'Track verification jobs you responded to and see whether clients accepted, rejected, or are still reviewing them.'
  ];

  // Debounced search handler - only triggers API after 3+ characters
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Only search if 3+ characters or empty (to reset)
    if (value.length >= 3 || value.length === 0) {
      const timer = setTimeout(() => {
        setFilters(prev => ({ ...prev, searchText: value }));
      }, 500); // 500ms debounce
      setDebounceTimer(timer);
    }
  }, [debounceTimer]);

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      minFee: '',
      maxFee: '',
      city: '',
      verificationType: '',
      priority: '',
      datePosted: '',
      searchText: '',
      sortBy: 'created_at'
    });
    setSearchInput('');
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '' && v !== 'created_at').length;

  useEffect(() => {
    fetchVerifierProfile();
  }, [user]);

  useEffect(() => {
    if (fixedTab !== null && fixedTab !== activeTab) {
      setActiveTab(fixedTab);
    }
  }, [fixedTab, activeTab]);

  // Check profile completeness when verifier profile is loaded
  useEffect(() => {
    if (verifierProfile) {
      checkProfileCompleteness();
    }
  }, [verifierProfile]);

  // Fetch user's responses on initial load to track which jobs already have responses
  useEffect(() => {
    if (user && verifierProfile) {
      fetchMyQuotes(false); // Don't show loading for background fetch
    }
  }, [user, verifierProfile]);

  useEffect(() => {
    if (verifierProfile) {
      if (activeTab === 0) {
        fetchMarketplaceRequests();
      } else if (activeTab === 1) {
        fetchRecommendedRequests();
      } else if (activeTab === 2) {
        fetchMyQuotes(true);
      }
    }
  }, [activeTab, filters, verifierProfile]);

  // Create a Set of request IDs that the user has already responded to.
  // The backend prevents a second response even after withdrawal, so the UI mirrors that rule.
  const submittedRequestIds = useMemo(() => {
    return new Set(
      myQuotes
        .map(quote => quote.verification_request_id)
    );
  }, [myQuotes]);

  const checkProfileCompleteness = async () => {
    setCheckingProfile(true);
    try {
      const completeness = await verifierService.getMyProfileCompleteness();
      setProfileCompleteness(completeness);
    } catch (err) {
      console.error('Error checking profile completeness:', err);
      // If profile doesn't exist, set as incomplete
      setProfileCompleteness({
        isComplete: false,
        canAccess: false,
        completionPercentage: 0,
        missingItems: ['Complete your verifier profile to respond to verification jobs']
      });
    } finally {
      setCheckingProfile(false);
    }
  };

  const fetchVerifierProfile = async () => {
    try {
      const response = await api.get(`/verifiers/${user.id}`);
      if (response.data.success) {
        setVerifierProfile(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching verifier profile:', err);
    }
  };

  const fetchMarketplaceRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        verifierTier: verifierProfile?.tier,
        ...filters
      };
      const response = await api.get('/verifier/marketplace', { params });
      if (response.data.success) {
        setRequests(response.data.data.requests || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch marketplace requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendedRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/verifier/marketplace/recommended/${user.id}`);
      if (response.data.success) {
        setRecommendedRequests(response.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch recommended requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyQuotes = async (showLoading = false) => {
    // Don't set loading state if we're just fetching for tracking purposes
    if (showLoading) {
    setLoading(true);
    }
    if (showLoading) {
    setError('');
    }
    try {
      const response = await api.get(`/verifier/marketplace/proposals/${user.id}`);
      if (response.data.success) {
        setMyQuotes(response.data.data || []);
      }
    } catch (err) {
      if (showLoading) {
      setError(err.response?.data?.message || 'Failed to fetch responses');
      }
    } finally {
      if (showLoading) {
      setLoading(false);
      }
    }
  };

  const handleOpenQuoteDialog = (request) => {
    // Check profile gate before allowing response submission
    if (!checkAction('apply')) {
      return; // Gate dialog will be shown by the hook
    }

    setSelectedRequest(request);

    // Prepopulate date from verification request's scheduling.requestedDate
    let prepopulatedDate = '';
    try {
      if (request.scheduling) {
        const scheduling = typeof request.scheduling === 'string'
          ? JSON.parse(request.scheduling)
          : request.scheduling;

        if (scheduling?.requestedDate) {
          const requestedDate = new Date(scheduling.requestedDate);
          if (!isNaN(requestedDate.getTime())) {
            prepopulatedDate = requestedDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          }
        } else if (scheduling?.scheduledDateTime) {
          const scheduledDate = new Date(scheduling.scheduledDateTime);
          if (!isNaN(scheduledDate.getTime())) {
            prepopulatedDate = scheduledDate.toISOString().split('T')[0];
          }
        }
      } else if (request.requested_date) {
        const requestedDate = new Date(request.requested_date);
        if (!isNaN(requestedDate.getTime())) {
          prepopulatedDate = requestedDate.toISOString().split('T')[0];
        }
      }
    } catch (err) {
      console.warn('Error parsing scheduling date:', err);
    }

    setQuoteForm({
      proposedFee: request.estimated_fee || '',
      availableDate: prepopulatedDate,
      message: ''
    });
    setQuoteDialog(true);
  };

  const handleCloseQuoteDialog = () => {
    setQuoteDialog(false);
    setSelectedRequest(null);
    setQuoteForm({
      proposedFee: '',
      availableDate: '',
      message: ''
    });
  };

  const handleSubmitQuote = async () => {
    setError('');
    setSuccess('');

    if (!quoteForm.proposedFee || !quoteForm.message) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const quoteData = {
        verificationRequestId: selectedRequest.id,
        verifierId: user.id,
        quoteAmount: parseFloat(quoteForm.proposedFee),
        availableDate: quoteForm.availableDate || null,
        proposalText: quoteForm.message,
        travelCost: 0 // Travel cost not implemented
      };

      const response = await api.post('/verifier/marketplace/proposals', quoteData);

      if (response.data.success) {
        setSuccess('Response sent successfully!');
        handleCloseQuoteDialog();
        fetchMarketplaceRequests(); // Refresh list
        fetchMyQuotes(); // Refresh responses to update submittedRequestIds
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Submit response error:', err);
      const errorData = err.response?.data;

      if (errorData?.requiresProfileCompletion) {
        const missingItems = errorData.completionStatus?.missingItems || [];
        const percentage = errorData.completionStatus?.completionPercentage || 0;

        let errorMessage = `Profile incomplete (${percentage}% complete). Please complete:\n`;
        errorMessage += missingItems.map(item => `• ${item}`).join('\n');

        setError(errorMessage);

        // Optionally redirect to profile setup
        setTimeout(() => {
          if (window.confirm('Your profile is incomplete. Would you like to complete it now?')) {
            window.location.href = '/dashboard'; // Will trigger wizard
          }
        }, 2000);
      } else {
        setError(errorData?.message || 'Failed to send response');
      }
    }
  };

  const handleWithdrawQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to withdraw this response?')) {
      return;
    }

    try {
      const response = await api.put(
        `/verifier/marketplace/proposals/${quoteId}/withdraw`,
        { verifierId: user.id }
      );

      if (response.data.success) {
        setSuccess('Response withdrawn successfully');
        fetchMyQuotes(false); // Refresh responses in background
        // Refresh marketplace requests to update button states
        if (activeTab === 0) {
          fetchMarketplaceRequests();
        } else if (activeTab === 1) {
          fetchRecommendedRequests();
        }
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to withdraw response');
    }
  };

  const handleMessageClient = async (clientId) => {
    try {
      const response = await messagingService.getOrCreateConversation(clientId);
      const conversationId = response.data?.id || response.id;
      navigate(`/messages/${conversationId}`);
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError('Failed to open messaging. Please try again.');
    }
  };

  const handleViewQuoteDetails = async (quote) => {
    setSelectedQuote(quote);
    setQuoteDetailsDialog(true);
    setLoadingQuoteDetails(true);
    setQuoteVerificationRequest(null);
    setError(''); // Clear any previous errors

    try {
      // Fetch the verification request details using the quote's verification_request_id
      // The backend returns vp.* which includes verification_request_id
      const requestId = quote.verification_request_id || quote.verificationRequestId;

      if (!requestId) {
        console.error('Quote object:', quote);
        setError('Verification request ID not found in quote');
        setLoadingQuoteDetails(false);
        return;
      }

      const response = await api.get(`/verification-requests/${requestId}`);

      if (response.data.success) {
        setQuoteVerificationRequest(response.data.data);
      } else {
        setError('Failed to load verification request details');
      }
    } catch (err) {
      console.error('Error fetching verification request details:', err);
      setError(err.response?.data?.message || 'Failed to load verification request details');
    } finally {
      setLoadingQuoteDetails(false);
    }
  };

  const canBidOnRequest = (request) => {
    // General verifiers cannot quote on professional requests
    if (request.required_tier === 'professional' && verifierProfile?.tier === 'general') {
      return false;
    }
    return true;
  };

  const renderRequestCard = (request, showMatchScore = false) => {
    const canRespond = canBidOnRequest(request);
    const hasSubmittedQuote = submittedRequestIds.has(request.id);
    const profileIncomplete = profileCompleteness && !profileCompleteness.canAccess;
    const requestDetails = request.request_details || {};

    return (
      <Card key={request.id} sx={{ mb: 2, position: 'relative' }}>
        {showMatchScore && request.matchScore && (
          <Chip
            label={`${request.matchScore}% Match`}
            color="primary"
            size="small"
            sx={{ position: 'absolute', top: 16, right: 16 }}
          />
        )}

        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Typography variant="h6" gutterBottom>
              {requestDetails.title || 'Verification Request'}
            </Typography>
            {request.required_tier && (
              <Chip
                label={request.required_tier}
                color={request.required_tier === 'professional' ? 'secondary' : 'default'}
                size="small"
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {requestDetails.description || 'No description available'}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney fontSize="small" color="primary" />
                <Typography variant="body2">
                  Fee: ${parseFloat(request.estimated_fee || 0).toFixed(2)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn fontSize="small" color="primary" />
                <Typography variant="body2">
                  {request.project_location || 'Location not specified'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Schedule fontSize="small" color="primary" />
                <Typography variant="body2">
                  Priority: {request.priority || 'normal'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Visibility fontSize="small" color="primary" />
                <Typography variant="body2">
                  {request.proposal_count || 0} responses
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {showMatchScore && request.matchReasons && request.matchReasons.length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Why this is a good match:
              </Typography>
              {request.matchReasons.map((reason, idx) => (
                <Typography key={idx} variant="caption" display="block" color="text.secondary">
                  • {reason}
                </Typography>
              ))}
            </Box>
          )}

          {!canRespond && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              General verifiers cannot respond to professional verification jobs
            </Alert>
          )}

          {hasSubmittedQuote && (
            <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle fontSize="small" />
              You have already responded to this verification job
            </Alert>
          )}
        </CardContent>

        <CardActions>
          <Tooltip
            title={profileIncomplete ? "Complete your profile to respond to verification jobs" : ""}
            arrow
          >
            <span>
              <Button
                size="small"
                variant={hasSubmittedQuote ? "outlined" : "contained"}
                startIcon={hasSubmittedQuote ? <CheckCircle /> : <Send />}
                onClick={() => handleOpenQuoteDialog(request)}
                disabled={!canRespond || hasSubmittedQuote || profileIncomplete}
                sx={{
                  ...((hasSubmittedQuote || profileIncomplete) && {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                  })
                }}
              >
                {hasSubmittedQuote ? 'Response Sent' : 'Respond to Job'}
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            color="primary"
            startIcon={<Visibility />}
            onClick={() => {
              setDetailsRequest(request);
              setDetailsDialog(true);
            }}
          >
            View Details
          </Button>
          {(request.client_id || request.clientId) && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleMessageClient(request.client_id || request.clientId)}
            >
              <Message />
            </IconButton>
          )}
        </CardActions>
      </Card>
    );
  };

  const renderQuoteCard = (quote) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'accepted': return 'success';
        case 'rejected': return 'error';
        case 'withdrawn': return 'default';
        case 'submitted': return 'info';
        default: return 'default';
      }
    };

    return (
      <Card key={quote.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Typography variant="h6">
              {quote.milestone_name}
            </Typography>
            <Chip
              label={quote.status}
              color={getStatusColor(quote.status)}
              size="small"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Project: {quote.project_title}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Project Owner Budget
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                ${parseFloat(quote.estimated_fee || 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your Fee
              </Typography>
              <Typography variant="h6" color={parseFloat(quote.quote_amount) <= parseFloat(quote.estimated_fee || 0) ? 'success.main' : 'warning.main'} sx={{ fontWeight: 'bold' }}>
                ${parseFloat(quote.quote_amount).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Estimated Time for Completion:</strong> {quote.estimated_hours} hours
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Submitted: {new Date(quote.submitted_at || quote.created_at).toLocaleDateString()}
              </Typography>
            </Grid>
              <Grid item xs={12}>
              <Box display="flex" gap={1} flexWrap="wrap">
                {quote.status === 'accepted' && (
                  <Chip
                    icon={<CheckCircle />}
                    label="Accepted"
                    color="success"
                    size="small"
                  />
                )}
                {quote.status === 'rejected' && (
                  <Chip
                    icon={<Cancel />}
                    label="Rejected"
                    color="error"
                    size="small"
                  />
                )}
                {quote.status === 'submitted' && (
                  <Chip
                    label="Pending Review"
                    color="info"
                    size="small"
                  />
                )}
                {quote.status === 'withdrawn' && (
                  <Chip
                    label="Withdrawn"
                    color="default"
                    size="small"
                  />
                )}
                {quote.client_viewed && (
                  <Chip label="Viewed by client" size="small" color="success" variant="outlined" />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        <CardActions>
          {quote.status === 'submitted' && (
            <Button
              size="small"
              color="error"
              onClick={() => handleWithdrawQuote(quote.id)}
            >
              Withdraw
            </Button>
          )}
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => handleViewQuoteDetails(quote)}
          >
            View Details
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: embedded ? 0 : 4, mb: embedded ? 0 : 4, px: embedded ? '0 !important' : undefined }}>
      {!embedded && (
        <>
          <Typography variant="h4" gutterBottom>
            Verification Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review client-posted verification work and send your price, availability, and message.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/verifier/available-verifications')}
            sx={{ mb: 3 }}
          >
            Find Work
          </Button>
        </>
      )}

      {/* Profile Incomplete Warning */}
      {!checkingProfile && profileCompleteness && !profileCompleteness.canAccess && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.href = '/verifier/profile'}
            >
              Complete Profile
            </Button>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            <strong>Profile Incomplete ({profileCompleteness.completionPercentage || 0}% Complete)</strong>
          </Typography>
          <Typography variant="body2">
            You must complete your profile and get approval before you can respond to verification jobs. Missing:
          </Typography>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {profileCompleteness.missingItems?.map((item, idx) => (
              <li key={idx}><Typography variant="body2">{item}</Typography></li>
            ))}
          </ul>
        </Alert>
      )}

      {verifierProfile && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Your Tier: {verifierProfile.tier}</strong>
          {verifierProfile.tier === 'general' &&
            ' - You can view all verification jobs but can only respond to general verification jobs'}
          {verifierProfile.tier === 'professional' &&
            ' - You can respond to both general and professional verification jobs'}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {!embedded && (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Verification Jobs" title={tabHelpText[0]} />
          <Tab
            title={tabHelpText[1]}
            label={
              <Badge badgeContent={recommendedRequests.length} color="primary">
                Recommended
              </Badge>
            }
          />
          <Tab
            title={tabHelpText[2]}
            label={
              <Badge badgeContent={myQuotes.length} color="secondary">
                My Responses
              </Badge>
            }
          />
        </Tabs>
      </Box>
      )}

      {!embedded && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {tabHelpText[activeTab]}
        </Alert>
      )}

      {activeTab < 2 && (
        <Paper sx={{ mb: 3, p: 2 }}>
          {/* Search Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search verification requests... (min 3 characters)"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleSearchChange('')}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText={searchInput.length > 0 && searchInput.length < 3 ?
                `Type ${3 - searchInput.length} more character(s) to search` : ''}
            />
            <Button
              variant="outlined"
              startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ minWidth: 140 }}
            >
              <FilterList sx={{ mr: 1 }} />
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
            {activeFilterCount > 0 && (
              <Button
                variant="text"
                color="error"
                onClick={handleClearFilters}
                startIcon={<Clear />}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* Expandable Filters */}
          <Collapse in={showFilters}>
            <Grid container spacing={2}>
              {/* Row 1: Location, Verification Type, Priority */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>City/Location</InputLabel>
                  <Select
                    value={filters.city}
                    label="City/Location"
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  >
                    {ZIMBABWE_CITIES.map(city => (
                      <MenuItem key={city} value={city === 'All Locations' ? '' : city}>
                        <LocationOn sx={{ mr: 1, fontSize: 18, color: 'action.active' }} />
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Verification Type</InputLabel>
                  <Select
                    value={filters.verificationType}
                    label="Verification Type"
                    onChange={(e) => setFilters({ ...filters, verificationType: e.target.value })}
                  >
                    {VERIFICATION_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filters.priority}
                    label="Priority"
                    onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 2: Fee Range, Date Posted, Sort By */}
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Min Fee ($)"
                  type="number"
                  value={filters.minFee}
                  onChange={(e) => setFilters({ ...filters, minFee: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max Fee ($)"
                  type="number"
                  value={filters.maxFee}
                  onChange={(e) => setFilters({ ...filters, maxFee: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Posted</InputLabel>
                  <Select
                    value={filters.datePosted}
                    label="Date Posted"
                    onChange={(e) => setFilters({ ...filters, datePosted: e.target.value })}
                  >
                    {DATE_POSTED_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Schedule sx={{ mr: 1, fontSize: 18, color: 'action.active' }} />
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={filters.sortBy}
                    label="Sort By"
                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                  >
                    <MenuItem value="created_at">Newest First</MenuItem>
                    <MenuItem value="estimated_fee">Highest Fee</MenuItem>
                    <MenuItem value="priority">Priority</MenuItem>
                    <MenuItem value="proposal_count">Fewest Responses</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      )}



      {loading ? (
        <Box display="flex" flexDirection="column" gap={3} p={1}>
          {[0, 1, 2].map((i) => (
            <Card key={i} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box flex={1}>
                    <Skeleton variant="text" width="70%" height={28} />
                    <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                  </Box>
                  <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="text" width="90%" height={18} />
                <Skeleton variant="text" width="60%" height={18} />
                <Box display="flex" gap={1} mt={2}>
                  <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: '12px' }} />
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '12px' }} />
                  <Skeleton variant="rectangular" width={120} height={24} sx={{ borderRadius: '12px' }} />
                </Box>
              </CardContent>
              <Box px={2} pb={2} display="flex" justifyContent="flex-end" gap={1}>
                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
        <Box>
          {activeTab === 0 && requests.map(request => renderRequestCard(request))}
          {activeTab === 1 && recommendedRequests.map(request => renderRequestCard(request, true))}
          {activeTab === 2 && (() => {
            // Filter and sort responses
            let filteredQuotes = [...myQuotes];

            // Apply status filter
            if (quoteFilters.status) {
              filteredQuotes = filteredQuotes.filter(p => p.status === quoteFilters.status);
            }

            // Apply search filter
            if (quoteFilters.searchText) {
              const searchLower = quoteFilters.searchText.toLowerCase();
              filteredQuotes = filteredQuotes.filter(p =>
                (p.project_title && p.project_title.toLowerCase().includes(searchLower)) ||
                (p.milestone_name && p.milestone_name.toLowerCase().includes(searchLower))
              );
            }

            // Apply quote range filter
            if (quoteFilters.minQuote) {
              filteredQuotes = filteredQuotes.filter(p =>
                parseFloat(p.quote_amount) >= parseFloat(quoteFilters.minQuote)
              );
            }
            if (quoteFilters.maxQuote) {
              filteredQuotes = filteredQuotes.filter(p =>
                parseFloat(p.quote_amount) <= parseFloat(quoteFilters.maxQuote)
              );
            }

            // Apply date filter
            if (quoteFilters.datePosted) {
              const now = new Date();
              const cutoffDate = new Date();
              switch (quoteFilters.datePosted) {
                case '24h':
                  cutoffDate.setHours(now.getHours() - 24);
                  break;
                case '7d':
                  cutoffDate.setDate(now.getDate() - 7);
                  break;
                case '30d':
                  cutoffDate.setDate(now.getDate() - 30);
                  break;
              }
              filteredQuotes = filteredQuotes.filter(p => {
                const quoteDate = new Date(p.submitted_at || p.created_at);
                return quoteDate >= cutoffDate;
              });
            }

            // Apply sorting
            filteredQuotes.sort((a, b) => {
              switch (quoteFilters.sortBy) {
                case 'quote_amount':
                  return parseFloat(b.quote_amount) - parseFloat(a.quote_amount);
                case 'status':
                  return a.status.localeCompare(b.status);
                case 'created_at':
                default:
                  return new Date(b.submitted_at || b.created_at) - new Date(a.submitted_at || a.created_at);
              }
            });

            return filteredQuotes.map(quote => renderQuoteCard(quote));
          })()}

          {((activeTab === 0 && requests.length === 0) ||
            (activeTab === 1 && recommendedRequests.length === 0)) && (
            <Alert severity="info">No items to display</Alert>
          )}
        </Box>
      )}

      {/* AI-Powered Quote Dialog */}
      <AIQuoteDialog
        open={quoteDialog}
        onClose={handleCloseQuoteDialog}
        selectedRequest={selectedRequest}
        quoteForm={quoteForm}
        setQuoteForm={setQuoteForm}
        onSubmit={handleSubmitQuote}
      />

      {/* OLD DIALOG - REPLACED WITH AI VERSION (removed) */}

      {/* Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setDetailsDialog(false)} />
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {detailsRequest?.request_details?.title || 'Verification Request'}
          {detailsRequest && (
            <Chip
              label={detailsRequest.status}
              size="small"
              color={detailsRequest.status === 'pending' ? 'warning' : 'default'}
              sx={{ textTransform: 'capitalize' }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailsRequest && (
            <Box>
              <Typography variant="body1" paragraph>
                {detailsRequest.request_details?.description || 'No description available'}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Estimated Fee</Typography>
                  <Typography variant="h6" sx={{ color: '#43B581' }}>
                    ${parseFloat(detailsRequest.estimated_fee || 0).toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                  <Chip
                    label={detailsRequest.priority || 'Normal'}
                    size="small"
                    sx={{
                      bgcolor: detailsRequest.priority === 'high' ? '#e74c3c' :
                               detailsRequest.priority === 'medium' ? '#f39c12' : '#43B581',
                      color: 'white', fontWeight: 600, textTransform: 'capitalize', mt: 0.5
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  <Typography variant="body1">
                    {detailsRequest.location?.address || detailsRequest.project_location || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Responses</Typography>
                  <Typography variant="body1">
                    {detailsRequest.proposal_count || 0} received
                  </Typography>
                </Grid>

                {detailsRequest.request_details?.verificationScope &&
                 detailsRequest.request_details.verificationScope.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Verification Scope
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {detailsRequest.request_details.verificationScope.map((scope, idx) => (
                        <Chip key={idx} label={scope} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body2">
                    {new Date(detailsRequest.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
          <Tooltip
            title={profileCompleteness && !profileCompleteness.canAccess ? "Complete your profile to respond to verification jobs" : ""}
            arrow
          >
            <span>
              <Button
                variant={detailsRequest && submittedRequestIds.has(detailsRequest.id) ? "outlined" : "contained"}
                color="primary"
                startIcon={detailsRequest && submittedRequestIds.has(detailsRequest.id) ? <CheckCircle /> : <Send />}
                onClick={() => {
                  setDetailsDialog(false);
                  if (!submittedRequestIds.has(detailsRequest.id)) {
                  handleOpenQuoteDialog(detailsRequest);
                  }
                }}
                disabled={
                  (detailsRequest && submittedRequestIds.has(detailsRequest.id)) ||
                  (profileCompleteness && !profileCompleteness.canAccess)
                }
                sx={{
                  ...((detailsRequest && submittedRequestIds.has(detailsRequest.id)) ||
                      (profileCompleteness && !profileCompleteness.canAccess)) && {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                  }
                }}
              >
                {detailsRequest && submittedRequestIds.has(detailsRequest.id) ? 'Response Sent' : 'Respond to Job'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Response Details Dialog */}
      <Dialog
        open={quoteDetailsDialog}
        onClose={() => {
          setQuoteDetailsDialog(false);
          setSelectedQuote(null);
          setQuoteVerificationRequest(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => {
          setQuoteDetailsDialog(false);
          setSelectedQuote(null);
          setQuoteVerificationRequest(null);
        }} />
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Response Details</Typography>
            {selectedQuote && (
              <Chip
                label={selectedQuote.status}
                color={
                  selectedQuote.status === 'accepted' ? 'success' :
                  selectedQuote.status === 'rejected' ? 'error' :
                  selectedQuote.status === 'submitted' ? 'info' : 'default'
                }
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedQuote ? (
            <Box>
              {/* Response Information */}
              <Typography variant="h6" gutterBottom>
                Your Response
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Project Owner Budget</Typography>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    ${parseFloat(quoteVerificationRequest?.estimated_fee || quoteVerificationRequest?.payment?.verificationFee || selectedQuote.estimated_fee || 0).toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Your Fee</Typography>
                  <Typography variant="h6" color={parseFloat(selectedQuote.quote_amount || 0) <= parseFloat(quoteVerificationRequest?.estimated_fee || quoteVerificationRequest?.payment?.verificationFee || selectedQuote.estimated_fee || 0) ? 'success.main' : 'warning.main'} sx={{ fontWeight: 'bold' }}>
                    ${parseFloat(selectedQuote.quote_amount || 0).toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Estimated Time for Completion</Typography>
                  <Typography variant="body1">
                    {selectedQuote.estimated_hours || 'N/A'} hours
                  </Typography>
                </Grid>

                {selectedQuote.available_date && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Verification Date</Typography>
                    <Typography variant="body1">
                      {new Date(selectedQuote.available_date).toLocaleDateString()}
                      {selectedQuote.proposed_time && ` at ${selectedQuote.proposed_time}`}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Submitted</Typography>
                  <Typography variant="body1">
                    {new Date(selectedQuote.submitted_at || selectedQuote.created_at).toLocaleString()}
                  </Typography>
                </Grid>

                {selectedQuote.proposal_text && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Response Message
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {selectedQuote.proposal_text}
                    </Typography>
                  </Grid>
                )}

                {selectedQuote.approach && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Your Approach
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {selectedQuote.approach}
                    </Typography>
                  </Grid>
                )}

                {selectedQuote.equipment_list && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Equipment
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Array.isArray(selectedQuote.equipment_list)
                        ? selectedQuote.equipment_list.map((item, idx) => (
                            <Chip key={idx} label={item} size="small" variant="outlined" />
                          ))
                        : typeof selectedQuote.equipment_list === 'string'
                          ? selectedQuote.equipment_list.split(',').map((item, idx) => (
                              <Chip key={idx} label={item.trim()} size="small" variant="outlined" />
                            ))
                          : null
                      }
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Verification Request Information */}
              {loadingQuoteDetails ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading verification request details...
                  </Typography>
                </Box>
              ) : quoteVerificationRequest ? (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Verification Request Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {quoteVerificationRequest.request_details?.title || selectedQuote.milestone_name || 'Verification Request'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {quoteVerificationRequest.request_details?.description || 'No description available'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Project</Typography>
                      <Typography variant="body1">
                        {selectedQuote.project_title || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Milestone</Typography>
                      <Typography variant="body1">
                        {selectedQuote.milestone_name || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">
                        {quoteVerificationRequest.location?.address ||
                         selectedQuote.project_location ||
                         quoteVerificationRequest.project_location ||
                         'Not specified'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                      <Typography variant="body1">
                        {quoteVerificationRequest.priority || selectedQuote.priority || 'Normal'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Estimated Fee</Typography>
                      <Typography variant="body1" color="primary">
                        ${parseFloat(quoteVerificationRequest.estimated_fee || selectedQuote.estimated_fee || 0).toFixed(2)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip
                        label={quoteVerificationRequest.status || 'pending'}
                        color={quoteVerificationRequest.status === 'completed' ? 'success' : 'default'}
                        size="small"
                      />
                    </Grid>

                    {quoteVerificationRequest.request_details?.verificationScope &&
                     quoteVerificationRequest.request_details.verificationScope.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Verification Scope
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {quoteVerificationRequest.request_details.verificationScope.map((scope, idx) => (
                            <Chip key={idx} label={scope} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Request Created</Typography>
                      <Typography variant="body2">
                        {quoteVerificationRequest.created_at
                          ? new Date(quoteVerificationRequest.created_at).toLocaleString()
                          : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              ) : error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Could not load verification request details. Please try again later.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setQuoteDetailsDialog(false);
            setSelectedQuote(null);
            setQuoteVerificationRequest(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Gate Dialog */}
      <ProfileGateDialog
        open={showGateDialog}
        onClose={closeGateDialog}
        action={gateAction}
      />
    </Container>
  );
};

export default VerifierMarketplacePage;
